import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_API_URL;

interface Bookie {
  id: number;
  name: string;
  email: string;
  city: string;
  state: string;
  country: string;
  created_at: string;
  league_count: number;
}

const MasterBookies: React.FC = () => {
  const { t } = useTranslation();

  const [bookies, setBookies] = useState<Bookie[]>([]);
  const [loading, setLoading] = useState(true);

  // Edit bookie modal
  const [showEditBookieModal, setShowEditBookieModal] = useState(false);
  const [editBookieTarget, setEditBookieTarget] = useState<Bookie | null>(null);
  const [editBookieForm, setEditBookieForm] = useState({ name: '', email: '' });
  const [savingBookieEdit, setSavingBookieEdit] = useState(false);
  const [editBookieError, setEditBookieError] = useState<string | null>(null);

  // Set password modal for bookie
  const [showBookiePwdModal, setShowBookiePwdModal] = useState(false);
  const [bookiePwdTarget, setBookiePwdTarget] = useState<{ id: number; name: string; email: string } | null>(null);
  const [bookiePwdValue, setBookiePwdValue] = useState('');
  const [bookiePwdLoading, setBookiePwdLoading] = useState(false);
  const [bookiePwdError, setBookiePwdError] = useState<string | null>(null);
  const [bookiePwdSuccess, setBookiePwdSuccess] = useState(false);

  useEffect(() => {
    fetchBookies();
  }, []);

  const fetchBookies = async () => {
    try {
      setLoading(true);
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/master/bookies/bookies`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBookies(data.bookies);
      }
    } catch (error) {
      console.error('Failed to fetch bookies:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const openEditBookieModal = (bookie: Bookie, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditBookieTarget(bookie);
    setEditBookieForm({ name: bookie.name, email: bookie.email });
    setEditBookieError(null);
    setShowEditBookieModal(true);
  };

  const handleSaveBookieEdit = async () => {
    if (!editBookieTarget) return;
    setSavingBookieEdit(true);
    setEditBookieError(null);
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/master/bookies/bookies/${editBookieTarget.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editBookieForm)
      });
      if (response.ok) {
        setBookies(prev => prev.map(b => b.id === editBookieTarget.id ? { ...b, ...editBookieForm } : b));
        setShowEditBookieModal(false);
      } else {
        const data = await response.json();
        setEditBookieError(data.error || 'Failed to update bookie.');
      }
    } catch {
      setEditBookieError('Network error. Please try again.');
    } finally {
      setSavingBookieEdit(false);
    }
  };

  const openBookiePwdModal = (bookie: { id: number; name: string; email: string }, e: React.MouseEvent) => {
    e.stopPropagation();
    setBookiePwdTarget(bookie);
    setBookiePwdValue('');
    setBookiePwdError(null);
    setBookiePwdSuccess(false);
    setShowBookiePwdModal(true);
  };

  const handleSetBookiePassword = async () => {
    if (!bookiePwdTarget) return;
    if (!bookiePwdValue || bookiePwdValue.length < 6) {
      setBookiePwdError('Password must be at least 6 characters.');
      return;
    }
    setBookiePwdLoading(true);
    setBookiePwdError(null);
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/master/bookies/bookies/${bookiePwdTarget.id}/set-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password: bookiePwdValue })
      });
      if (response.ok) {
        setBookiePwdSuccess(true);
      } else {
        const data = await response.json();
        setBookiePwdError(data.error || 'Failed to set password.');
      }
    } catch {
      setBookiePwdError('Network error. Please try again.');
    } finally {
      setBookiePwdLoading(false);
    }
  };

  return (
    <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 border border-white/20 max-h-[calc(100vh-300px)] overflow-y-auto">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-3xl font-bold text-white">
          Bookies ({bookies.length})
        </h3>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-400"></div>
          <p className="text-blue-200 mt-4">{t('common.loading')}</p>
        </div>
      ) : bookies.length === 0 ? (
        <div className="text-center py-12">
          <span className="text-6xl">🪙</span>
          <p className="text-blue-200 mt-4 text-lg">No bookies found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {bookies.map((bookie) => (
            <div
              key={bookie.id}
              className="bg-gradient-to-br from-blue-600/20 to-cyan-600/20 border-2 border-blue-400/30 rounded-lg p-6 hover:border-blue-400/60 transition-all"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-xl">
                    {bookie.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => openEditBookieModal(bookie, e)}
                    title="Edit bookie"
                    className="text-xs px-2 py-1 bg-blue-500/20 hover:bg-blue-500/40 border border-blue-400/40 text-blue-300 rounded transition"
                  >
                    ✏️ Edit
                  </button>
                  <button
                    onClick={(e) => openBookiePwdModal(bookie, e)}
                    className="text-xs px-2 py-1 bg-cyan-500/20 hover:bg-cyan-500/40 border border-cyan-400/40 text-cyan-300 rounded transition"
                  >
                    🔑 Pwd
                  </button>
                  <span className="text-blue-300 font-mono text-sm">#{bookie.id}</span>
                </div>
              </div>

              <h4 className="text-white font-bold text-xl mb-2">{bookie.name}</h4>

              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <span className="text-blue-400">📧</span>
                  <p className="text-blue-200 text-sm truncate">{bookie.email}</p>
                </div>

               

                <div className="bg-white/10 p-2 rounded mt-2">
                  <p className="text-blue-200 text-xs font-semibold mb-1">Leagues managed:</p>
                  <p className="text-white text-lg font-bold">{bookie.league_count}</p>
                </div>

                <div className="flex items-center space-x-2">
                  <span className="text-blue-400">📅</span>
                  <p className="text-blue-300 text-xs">Joined: {formatDate(bookie.created_at)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Bookie Modal */}
      {showEditBookieModal && editBookieTarget && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowEditBookieModal(false)}>
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 max-w-md w-full mx-4 border border-white/20" onClick={e => e.stopPropagation()}>
            <h2 className="text-2xl font-bold text-white mb-6">✏️ Edit Bookie</h2>
            {editBookieError && (
              <p className="text-red-300 text-sm mb-4 bg-red-500/20 p-3 rounded-lg">{editBookieError}</p>
            )}
            <div className="space-y-4">
              <div>
                <label className="block text-blue-200 text-sm font-semibold mb-1">Name</label>
                <input
                  type="text"
                  value={editBookieForm.name}
                  onChange={e => setEditBookieForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-blue-400"
                />
              </div>
              <div>
                <label className="block text-blue-200 text-sm font-semibold mb-1">Email</label>
                <input
                  type="email"
                  value={editBookieForm.email}
                  onChange={e => setEditBookieForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-blue-400"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleSaveBookieEdit}
                disabled={savingBookieEdit}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white py-3 rounded-lg font-bold transition"
              >
                {savingBookieEdit ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => setShowEditBookieModal(false)}
                className="flex-1 bg-white/10 hover:bg-white/20 text-white py-3 rounded-lg font-bold transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Set Password Modal for Bookie */}
      {showBookiePwdModal && bookiePwdTarget && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowBookiePwdModal(false)}>
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 max-w-md w-full mx-4 border border-white/20" onClick={e => e.stopPropagation()}>
            <h2 className="text-2xl font-bold text-white mb-2">🔑 Set Password</h2>
            <p className="text-blue-200 text-sm mb-6">
              Setting a password for <strong className="text-white">{bookiePwdTarget.name}</strong>{' '}
              <span className="text-blue-300">({bookiePwdTarget.email})</span>
            </p>
            {bookiePwdSuccess ? (
              <div className="text-center py-4">
                <p className="text-green-300 text-lg font-semibold mb-2">✅ Password set successfully!</p>
                <p className="text-blue-200 text-sm mb-6">An email was sent to {bookiePwdTarget.email} with their new password.</p>
                <button
                  onClick={() => setShowBookiePwdModal(false)}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-bold transition"
                >
                  Done
                </button>
              </div>
            ) : (
              <>
                {bookiePwdError && (
                  <p className="text-red-300 text-sm mb-4 bg-red-500/20 p-3 rounded-lg">{bookiePwdError}</p>
                )}
                <div className="mb-6">
                  <label className="block text-blue-200 text-sm font-semibold mb-1">New Password</label>
                  <input
                    type="text"
                    value={bookiePwdValue}
                    onChange={e => setBookiePwdValue(e.target.value)}
                    placeholder="Min. 6 characters"
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-blue-400"
                  />
                  <p className="text-blue-300 text-xs mt-1">The bookie will be emailed their new password and told to change it.</p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleSetBookiePassword}
                    disabled={bookiePwdLoading}
                    className="flex-1 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white py-3 rounded-lg font-bold transition"
                  >
                    {bookiePwdLoading ? 'Setting...' : 'Set Password'}
                  </button>
                  <button
                    onClick={() => setShowBookiePwdModal(false)}
                    className="flex-1 bg-white/10 hover:bg-white/20 text-white py-3 rounded-lg font-bold transition"
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterBookies;
